import { LoginIcon, LogoutIcon, SparklesIcon } from '@heroicons/react/outline'
import { CheckIcon, XIcon } from '@heroicons/react/solid'
import React, { useState } from 'react'
import {
  Button,
  Container,
  Dropdown,
  Element,
  Icon,
  Message,
  Navbar,
  Tag,
} from 'react-bulma-components'
import { Size } from 'react-bulma-components/src/components'
import { Link, LinkProps, useNavigate } from 'react-router-dom'
import { UserRole, UserRoleKind } from '../../../../backend/src/services/resources/users/user.model'
import { usePendingEmployerInvitationsCount } from '../../hooks/use-pending-employer-invitations-count'
import { usePendingInvitationsCount } from '../../hooks/use-pending-invitations-count'
//@ts-ignore
import * as logo from '../../images/team-tim-avatar.webp'
import locales from '../../../../locales'
import useStore from '../../store'
import { Organization } from '../../api/organizations'
import { SessionSlice } from '../../store/session'
import { User } from '../../api/users'
import { getRoleColorHandle } from '../../utils/colors'
import Avatar from '../avatar/avatar'
import LoginAs from '../login-as/login-as'
import Modal, { useModal } from '../modal'
import Protected from '../protected/protected'
import { AndroidMobileAppIcon, AppleMobileAppIcon, HelpIcon, SettingsIcon } from '../icons'
import { useHasNewChangelog } from '../../hooks/use-has-new-changelog'
import { useIsDevEnvironment } from '../../hooks/use-is-dev-environment'
import config from '../../config'
import { trimTextIfLongerThan } from '../../utils/texts'
import OrganizationOptionProtected from '../protected/organization-option-protected'

interface PendingInvationsTagProps {
  count: number
  style?: React.CSSProperties
}

const Header: React.FunctionComponent = () => {
  const [displayMenu, setDisplayMenu] = useState(false)
  const currentUser = useStore(state => state.session.currentUser)
  const currentUserRole = useStore(state => state.session.currentUserRole)
  const currentOrganization = useStore(state => state.session.currentOrganization)
  const changeCurrentUserRole = useStore(state => state.session.changeCurrentRole)
  const logout = useStore(state => state.session.logout)
  const loggedAs = useStore(state => state.session.loggedAs)
  const pendingInvitationsCount = usePendingInvitationsCount()
  const pendingEmployerInvitationsCount = usePendingEmployerInvitationsCount()
  const navigate = useNavigate()
  const isDevEnvironment = useIsDevEnvironment()
  const { hasNew: hasNewChangelog } = useHasNewChangelog()

  const modal = useModal()

  const realUserRole = loggedAs?.organization?.type ? 'superAdmin' : currentUserRole

  const changeCurrentRole = (role: UserRole) => {
    navigate('/')
    changeCurrentUserRole(role)
  }

  return (
    <Element renderAs="header">
      <Navbar
        active={displayMenu}
        fixed="top"
        data-test="header-menu"
        backgroundColor={realUserRole === 'superAdmin' ? 'super-admin-light' : 'primary-light'}
      >
        <Container breakpoint={'fluid'}>
          <Navbar.Brand>
            <Navbar.Item renderAs={Link} to="/" p={1}>
              {isDevEnvironment ? (
                <Message color={'warning'}>
                  <Message.Body>DEV</Message.Body>
                </Message>
              ) : (
                <img src={logo} alt="TeamTim" />
              )}
            </Navbar.Item>
            <Navbar.Item renderAs="div">
              {!loggedAs ? (
                <Element renderAs="span" textColor={`primary`} textWeight="bold">
                  {(currentOrganization?.name ?? realUserRole === 'worker')
                    ? trimTextIfLongerThan(currentUser?.firstName + ' ' + currentUser?.lastName, 20)
                    : locales.users.roles[realUserRole]}
                </Element>
              ) : (
                <LoggedAsStatusItem changeCurrentRole={changeCurrentRole} />
              )}
            </Navbar.Item>
            <Protected roles={['employerMember', 'interimAgencyMember', 'superAdmin']}>
              <HeaderItem
                to="/whatsnew"
                data-tooltip="Nouveautés"
                className="has-tooltip-bottom"
                data-test="whats-new-link"
                style={{ position: 'relative' }}
              >
                <Icon textColor="danger" className={hasNewChangelog ? 'whats-new-pulse' : ''}>✨</Icon>
                {hasNewChangelog && (
                  <Element
                    renderAs="span"
                    data-test="whats-new-dot"
                    className="whats-new-dot"
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: '#f14668',
                    }}
                  />
                )}
              </HeaderItem>
            </Protected>
            <Element
              renderAs="button"
              role="button"
              className="navbar-burger"
              onClick={() => setDisplayMenu(!displayMenu)}
            >
              <span aria-hidden="true"></span>
              <span aria-hidden="true"></span>
              <span aria-hidden="true"></span>
              <span aria-hidden="true"></span>
            </Element>
          </Navbar.Brand>
          <Navbar.Menu>
            <Navbar.Container align="right">
              <Protected roles={['worker']}>
                <HeaderItem
                  href={config.mobileApps.apple.store.url}
                  data-tooltip="App mobile Apple"
                  className="has-tooltip-bottom"
                >
                  <Icon size={'small'}>
                    <AppleMobileAppIcon />
                  </Icon>
                </HeaderItem>
                <HeaderItem
                  href={config.mobileApps.android.store.url}
                  data-tooltip="App mobile Android"
                  className="has-tooltip-bottom"
                >
                  <Icon size={'small'}>
                    <AndroidMobileAppIcon />
                  </Icon>
                </HeaderItem>
              </Protected>
              <Protected roles={['superAdmin', 'worker', 'interimAgencyMember', 'employerMember']}>
                <HeaderItem bold label="Tableau de Bord" to="/" />
                <Protected
                  roles={['employerMember', 'interimAgencyMember']}
                  action={{ resource: 'missions', name: 'read' }}
                >
                  <HeaderItem bold label="Récapitulatif" to="/summary" />
                </Protected>
                <Protected
                  roles={['employerMember', 'interimAgencyMember']}
                  action={{ resource: 'workersRequests', name: 'read' }}
                >
                  <HeaderItem bold label="Demandes d'Intérimaires" to="/workers-requests" />
                </Protected>
                <MobileNavbarSeparator />
                <Protected roles={['employerMember', 'interimAgencyMember', 'superAdmin']}>
                  <Navbar.Item hoverable data-test="organization-submenu">
                    <Navbar.Link textWeight="bold">
                      {currentOrganization?.type === 'employer'
                        ? 'Mon Entreprise'
                        : currentOrganization?.type === 'interimAgency'
                          ? 'Mon Agence'
                          : locales.users.roles[realUserRole]}
                    </Navbar.Link>
                    <Navbar.Dropdown right>
                      <Protected roles={['employerMember', 'interimAgencyMember']}>
                        <HeaderItemTitle label="Missions" />
                        <Protected
                          roles={['employerMember', 'interimAgencyMember']}
                          action={{ resource: 'missions', name: 'read' }}
                        >
                          <HeaderItem label="Missions" to="/missions" />
                        </Protected>
                        <Protected
                          roles={['employerMember']}
                          action={{ resource: 'missions', name: 'read' }}
                        >
                          <HeaderItem label="Journées de travail" to="/work-periods" />
                        </Protected>
                        <Protected
                          roles={['employerMember']}
                          action={{ resource: 'clockings', name: 'read' }}
                        >
                          <HeaderItem label="Pointage" to="/clockings" />
                        </Protected>
                        <HeaderItemTitle label="Finances" />
                        <Protected
                          roles={['employerMember', 'interimAgencyMember']}
                          action={{ resource: 'periodSummaries', name: 'read' }}
                        >
                          <HeaderItem label="Pré-Facturation" to="/pre-billings" />
                        </Protected>
                        <Protected
                          roles={['employerMember']}
                          action={{ resource: 'missions', name: 'read' }}
                        >
                          <OrganizationOptionProtected
                            options={[{ organizationType: 'employer', option: 'biPart' }]}
                          >
                            <HeaderItem label="Paie" to="/payroll" />
                          </OrganizationOptionProtected>
                        </Protected>
                      </Protected>
                      <Protected roles={['superAdmin']}>
                        <HeaderItemTitle label="Organisations" />
                        <HeaderItem label="Agences d'Intérim" to="/agencies" />
                        <HeaderItem label="Employeurs" to="/employers" />
                        <HeaderItemTitle label="Bornes de Pointage" />
                        <HeaderItem label="Bornes de Pointage" to="/terminals" />
                        <Dropdown.Divider touch={{ display: 'hidden' }} />
                        <Navbar.Item onClick={() => modal.setIsDisplayed(!modal.isDisplayed)}>
                          <Icon.Text flexWrap="nowrap">
                            <Icon color="grey">
                              <LoginIcon />
                            </Icon>
                            <Element renderAs="span">Me connecter en tant que...</Element>
                          </Icon.Text>
                        </Navbar.Item>
                        <Dropdown.Divider touch={{ display: 'hidden' }} />
                      </Protected>
                      <Protected
                        roles={['superAdmin']}
                        atLeastOneAction={[{ resource: 'users', name: 'create' }]}
                      >
                        <HeaderItemTitle label="Utilisateurs" />
                        <Protected roles={['superAdmin']}>
                          <HeaderItem
                            label="Super Admins"
                            to="/super-admins"
                            data-test="organization-submenu"
                          />
                        </Protected>
                      </Protected>
                    </Navbar.Dropdown>
                  </Navbar.Item>
                </Protected>
                <Protected
                  roles={['employerMember', 'interimAgencyMember']}
                  action={{ resource: 'organizations', name: 'update' }}
                >
                  <HeaderItem to="/settings" icon={SettingsIcon} info="Paramètres" />
                  <Element style={{ position: 'relative' }}>
                    <PendingInvationsTag
                      count={pendingEmployerInvitationsCount}
                      style={{ position: 'absolute', top: 2, right: 0 }}
                    />
                  </Element>
                </Protected>
                {!loggedAs ? (
                  <>
                    <MobileNavbarSeparator />
                    <Navbar.Item hoverable data-test="user-submenu">
                      <Navbar.Link textWeight="bold">
                        {currentUser && (
                          <>
                            <Element className="is-hidden-touch">
                              <Avatar user={currentUser} small={true} />
                            </Element>
                            <Element className="is-hidden-desktop">
                              <Avatar user={currentUser} small={false} />
                            </Element>
                          </>
                        )}
                        <PendingInvationsTag
                          count={pendingInvitationsCount}
                          style={{ position: 'absolute', top: 2, right: 30 }}
                        />
                      </Navbar.Link>
                      <Navbar.Dropdown right>
                        <UserRoles
                          user={currentUser}
                          currentRole={currentUserRole}
                          currentOrganization={currentOrganization}
                          changeCurrentRole={changeCurrentRole}
                        />
                        <HeaderItemTitle label="Mes Relations" />
                        <HeaderItem label="Mes Relations" to="/me/organizations">
                          <Element renderAs="span" ml={1}>
                            <PendingInvationsTag count={pendingInvitationsCount} />
                          </Element>
                        </HeaderItem>
                        <Dropdown.Divider touch={{ display: 'hidden' }} />
                        <HeaderItemTitle label="Paramètres" />
                        <HeaderItem label="Mes Informations" to="/me" />
                        <HeaderItem label="Notifications" to="/me/notifications" />
                        <Dropdown.Divider touch={{ display: 'hidden' }} />
                        <HeaderItem to="/help" label="Aide" icon={HelpIcon} />
                        <Protected roles={['employerMember', 'interimAgencyMember', 'superAdmin']}>
                          <HeaderItem to="/whatsnew" label="Nouveautés" icon={SparklesIcon} />
                        </Protected>
                        <Dropdown.Divider touch={{ display: 'hidden' }} />
                        <Navbar.Item data-test="user-logout" onClick={() => logout()}>
                          <Icon color="primary-dark">
                            <LogoutIcon />
                          </Icon>
                          <Element renderAs="span">Déconnexion</Element>
                        </Navbar.Item>
                      </Navbar.Dropdown>
                    </Navbar.Item>
                    {!loggedAs && (
                      <Protected roles={['superAdmin']}>
                        <Navbar.Item onClick={() => modal.setIsDisplayed(!modal.isDisplayed)}>
                          <Button color={'primary'}>
                            <Icon.Text flexWrap="nowrap">
                              <Icon>
                                <LoginIcon />
                              </Icon>
                            </Icon.Text>
                          </Button>
                        </Navbar.Item>
                      </Protected>
                    )}
                  </>
                ) : (
                  <Navbar.Item touch={{ display: 'hidden' }}>
                    <LoggedAsStatusItem changeCurrentRole={changeCurrentRole} size="smallest" />
                  </Navbar.Item>
                )}
              </Protected>
              <Protected roles={['guest']}>
                <HeaderItem bold label="Connexion" to="/login" />
                <HeaderItem bold label="Kiosque" to="/kiosk" />
              </Protected>
            </Navbar.Container>
          </Navbar.Menu>
        </Container>
      </Navbar>
      <Protected roles={['superAdmin']}>
        <Modal title="Me connecter en tant que..." actions={modal}>
          <LoginAs onSelect={() => modal.setIsDisplayed(false)} />
        </Modal>
      </Protected>
    </Element>
  )
}

interface LoggedAsStatusItemProps {
  size?: Size | 'smallest'
  changeCurrentRole(role: UserRole): void
}

const LoggedAsStatusItem: React.FC<LoggedAsStatusItemProps> = ({
  size = 'normal',
  changeCurrentRole,
}) => {
  const currentUser = useStore(state => state.session.currentUser)
  const currentUserRole = useStore(state => state.session.currentUserRole)
  const loggedAs = useStore(state => state.session.loggedAs)
  const loggedAsColorHandle = loggedAs?.organization?.type && getRoleColorHandle(currentUserRole)

  if (loggedAs?.organization?.name) {
    return (
      <Button
        color={loggedAsColorHandle}
        size={size === 'smallest' ? 'small' : size}
        onClick={() => {
          const superAdminRole = currentUser?.roles.find(role => role.kind === 'superAdmin')
          superAdminRole && changeCurrentRole(superAdminRole)
        }}
      >
        {size !== 'smallest' && (
          <Element renderAs="span" textWeight="bold" textColor={`${loggedAsColorHandle}-light`}>
            {loggedAs?.organization?.name}
          </Element>
        )}
        <Icon textColor={`${loggedAsColorHandle}-light`}>
          <XIcon />
        </Icon>
      </Button>
    )
  }
  return <></>
}

const PendingInvationsTag: React.FunctionComponent<PendingInvationsTagProps> = ({
  count,
  style,
}) => {
  if (count === 0) return <></>
  return (
    <Tag
      color={'warning'}
      style={style} //TODO: Tom ... : )
      title={`${count} invitation(s) en attente`}
      data-test={'pending-invitations-notification'}
    >
      {count}
    </Tag>
  )
}

const HeaderItemTitle: React.FunctionComponent<{ label: string }> = ({ label }) => (
  <Navbar.Item textWeight="bold" renderAs="div">
    {label}
  </Navbar.Item>
)

interface HeaderItemBaseProps {
  label?: React.ReactNode
  bold?: boolean
  icon?: React.FunctionComponent
  [key: string]: any
}

type HeaderItemRouterLink = HeaderItemBaseProps & {
  to: LinkProps['to']
}
type HeaderItemWebLink = HeaderItemBaseProps & {
  href: HTMLAnchorElement['href']
}

const HeaderItem: React.FunctionComponent<HeaderItemRouterLink | HeaderItemWebLink> = ({
  label,
  bold = false,
  to,
  href,
  icon,
  children,
  info,
  ...rest
}) => {
  const CustomIcon = icon
  const RenderAs = to ? Link : 'a'
  return (
    <Navbar.Item
      renderAs={RenderAs}
      {...(to ? { to } : undefined)}
      {...(href ? { href } : undefined)}
      {...(bold ? { textWeight: 'bold' } : {})}
      title={info}
      {...rest}
    >
      {CustomIcon ? (
        <Icon.Text>
          <Icon color="primary-dark">
            <CustomIcon />
          </Icon>
          <Element renderAs="span">{label}</Element>
        </Icon.Text>
      ) : (
        label
      )}
      {children}
    </Navbar.Item>
  )
}

interface UserRolesProps {
  user?: User
  currentRole: UserRoleKind | 'guest'
  currentOrganization?: SessionSlice['currentOrganization']
  changeCurrentRole: SessionSlice['changeCurrentRole']
}

const UserRoles: React.FunctionComponent<UserRolesProps> = ({
  user,
  currentRole,
  changeCurrentRole,
  currentOrganization,
}) => {
  const { commonRoles, organizationsRoles } = React.useMemo(() => {
    if (!user) return { commonRoles: [], organizationsRoles: [] }
    return {
      commonRoles: user.roles.filter(
        role =>
          (role.kind === 'worker' || role.kind === 'superAdmin') && role.status === 'accepted',
      ),
      organizationsRoles: user.roles.filter(
        role =>
          (role.kind === 'interimAgencyMember' || role.kind === 'employerMember') &&
          role.status === 'accepted',
      ),
    }
  }, [user, currentRole])

  if (commonRoles.length + organizationsRoles.length <= 1) return <></>

  return (
    <>
      {(commonRoles[0] || organizationsRoles[0]) &&
        commonRoles.length + organizationsRoles.length > 1 && (
          <>
            <HeaderItemTitle label="Changer de profil" />
            {commonRoles.map(role => (
              <Navbar.Item onClick={() => changeCurrentRole(role)} key={role._id as string}>
                <Icon.Text flexWrap="nowrap">
                  <Icon
                    mr={1}
                    color={`${getRoleColorHandle(role.kind)}-dark`}
                    backgroundColor={`${getRoleColorHandle(role.kind)}-light`}
                  >
                    {currentRole === role.kind ? <CheckIcon /> : ''}
                  </Icon>
                  {locales.users.roles[role.kind]}
                </Icon.Text>
              </Navbar.Item>
            ))}
            {organizationsRoles.map(role => {
              const organization = role.organization as Organization
              return (
                <Navbar.Item onClick={() => changeCurrentRole(role)} key={role._id as string}>
                  <Icon.Text flexWrap="nowrap">
                    <Icon
                      mr={1}
                      color={`${getRoleColorHandle(organization.type)}-dark`}
                      backgroundColor={`${getRoleColorHandle(organization.type)}-light`}
                    >
                      {currentOrganization && currentOrganization._id === organization._id ? (
                        <CheckIcon />
                      ) : (
                        ''
                      )}
                    </Icon>
                    {organization.name}
                  </Icon.Text>
                </Navbar.Item>
              )
            })}
          </>
        )}
      <Dropdown.Divider touch={{ display: 'hidden' }} />
    </>
  )
}

export default Header

const MobileNavbarSeparator = () => {
  return <Element renderAs="hr" my={1} desktop={{ display: 'hidden' }} />
}
